// Eligibility + preview for combining shipments in OrderDetail. Mirrors the
// server rules in shipper_api App\Services\Shipments\CombineShipments (which
// is authoritative). Hard rule: nothing that has synced to a 3PL (shipment or
// order `syncs`, i.e. a syncables row from Inland/Unis/Motili) can combine.

import { isBandBox } from "./band.mjs";

const list = (v) => (Array.isArray(v) ? v : []);

export function orderSyncedToThirdParty(order) {
  return list(order && order.syncs).length > 0;
}

export function shipmentSyncedToThirdParty(shipment) {
  return list(shipment && shipment.syncs).length > 0;
}

/** Order items on a shipment (OrderDetail loads them on the order, linked by item.shipment). */
export function shipmentItems(order, shipment) {
  if (!shipment) return [];
  const own = list(shipment.orderItems);
  if (own.length) return own;
  return list(order && order.orderItems).filter(
    (item) =>
      item && item.shipment && String(item.shipment.id) === String(shipment.id)
  );
}

/** Reasons this one shipment can never be part of a combine; [] when it can. */
export function shipmentBlockers(order, shipment) {
  const reasons = [];
  if (orderSyncedToThirdParty(order) || shipmentSyncedToThirdParty(shipment)) {
    reasons.push("Synced to a 3PL");
  }
  if (shipment.trackingId || shipment.shippedAt || shipment.label) {
    reasons.push("Already labelled, tracked or shipped");
  }
  return reasons;
}

/** Show the checkboxes at all? Not for 3PL-synced orders or single-shipment orders. */
export function combineAvailable(order) {
  return (
    !!order &&
    !orderSyncedToThirdParty(order) &&
    list(order.shipments).length >= 2
  );
}

const shipFromId = (s) =>
  (s.shipFromAddress && s.shipFromAddress.id) || s.shipFromAddressId || null;

/**
 * @returns {{canCombine: boolean, reasons: string[], hasBand: boolean, preview: object|null}}
 */
export function combineState(order, selectedIds) {
  const ids = list(selectedIds).map(String);
  const shipments = list(order && order.shipments).filter((s) =>
    ids.includes(String(s.id))
  );
  const reasons = [];

  if (shipments.length < 2) reasons.push("Select at least two shipments.");
  if (orderSyncedToThirdParty(order))
    reasons.push("This order has synced to a 3PL.");
  shipments.forEach((s) => {
    shipmentBlockers({}, s).forEach((r) => reasons.push("#" + s.id + ": " + r));
  });

  const froms = new Set(shipments.map(shipFromId).filter(Boolean).map(String));
  if (froms.size > 1) reasons.push("Shipments ship from different warehouses.");

  // Mixed shipping services are allowed: the server re-rates the kept
  // shipment and picks its service (PJ, 2026-10-15).

  const items = shipments.flatMap((s) => shipmentItems(order, s));
  const hasBand = items.some((i) => isBandBox(i.box));

  const num = (v) => parseFloat(v) || 0;
  const preview = shipments.length
    ? {
        keepId: shipments
          .map((s) => parseInt(s.id, 10))
          .sort((a, b) => a - b)[0],
        units: items.reduce(
          (sum, i) => sum + (parseInt(i.quantity, 10) || 0),
          0
        ),
        weight:
          Math.round(
            shipments.reduce((sum, s) => sum + num(s.weight), 0) * 100
          ) / 100,
        length: Math.max(...shipments.map((s) => num(s.length))),
        width: Math.max(...shipments.map((s) => num(s.width))),
        height:
          Math.round(
            shipments.reduce((sum, s) => sum + num(s.height), 0) * 100
          ) / 100,
        box: shipments[0].box || "",
      }
    : null;

  return { canCombine: reasons.length === 0, reasons, hasBand, preview };
}

/**
 * Turn the combine response `meta` into what the dialog shows: rates sorted
 * cheapest first, the selected one flagged, a human reason, and any warning.
 */
export function combineResult(meta) {
  const m = meta || {};
  const selectedId = m.selectedShippingServiceId
    ? String(m.selectedShippingServiceId)
    : null;
  const rates = (m.rates || [])
    .map((r) => ({
      ...r,
      id: String(r.id),
      selected: selectedId !== null && String(r.id) === selectedId,
    }))
    .sort((a, b) => {
      const ra = a.rate === null || a.rate === undefined ? Infinity : a.rate;
      const rb = b.rate === null || b.rate === undefined ? Infinity : b.rate;
      return ra - rb;
    });
  const selectionText =
    m.selection === "kept"
      ? "Kept the previously selected service (still offered)."
      : m.selection === "cheapest"
      ? "Previous service not offered; selected the cheapest rate."
      : null;
  return { rates, selectedId, selectionText, warning: m.warning || null };
}
